import React from 'react';
import settings from 'settings';
import { Modal } from 'antd';

const UpdateStates = Object.freeze({
  current: 1,
  updateAvailable: 2,
  updateAcknowledged: 3
});

const swUpdate = () => {
  navigator.serviceWorker.getRegistration('/')
    .then((registration) => {
      if (!registration) return;
      // noinspection JSIgnoredPromiseFromCall
      registration.update();
    });
};

const getLSItem = (key) => {
  const item = localStorage.getItem(key);
  return item && item === 'true';
};

const setLSItem = (key, value) => {
  localStorage.setItem(key, value.toString());
};

const handleRefresh = () => {
  window.location.reload(true);
};

export default function checkForUpdates(WrappedComponent) {
  return class extends React.Component {
    constructor(props) {
      super(props);
      this.checkUpdates = this.checkUpdates.bind(this);
      this.handleAcknowledged = this.handleAcknowledged.bind(this);
      this.handleVersionAcknowledged = this.handleVersionAcknowledged.bind(this);

      this.state = {
        updateState: UpdateStates.current,
        versionUpdateState: false
      };
    }

    componentDidMount() {
      // Remove V1 service workers
      // checks to see if there's a previous version of crew rostering
      // if a previous version is found, remove it's service worker
      try {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (let i = 0; i < registrations.length; i++) {
            const registration = registrations[i];
            // eslint-disable-next-line no-console
            // console.log('checkPreviousVersion.registration.scope', registration.scope);
            if (registration.scope === settings.PREVIOUS_URL()) {
              // check if there's an old version available
              // eslint-disable-next-line no-console
              console.log(`SW:${registration.scope} previous version service worker detected.`);
              registration.unregister()
                .then(() => {
                  // eslint-disable-next-line no-console
                  console.log(`SW:${settings.PREVIOUS_URL()} deregistered.`);
                  setLSItem('CR_AppVersionUpdate', true);
                });
            }
          }
        });
      }
      catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
      // No SW when local dev
      if (settings.APP_CONFIG.environment.toLowerCase() !== 'local') {
        if (this.swUpdateInterval) {
          clearInterval(this.swUpdateInterval);
        }
        if (this.updateInterval) {
          clearInterval(this.updateInterval);
        }

        // Service worker update (ie, check for new deploy)
        this.swUpdateInterval = setInterval(swUpdate, settings.SW_UPDATE_INTERVAL_MS);
        // Checks if SW has finished updating.
        // Not a heavy method, so this can run often.
        this.updateInterval = setInterval(this.checkUpdates, 1000);
      }
    }

    componentWillUnmount() {
      clearInterval(this.swUpdateInterval);
      clearInterval(this.updateInterval);
    }

    handleAcknowledged() {
      setLSItem('CR_AppUpdateAcknowledged', true);
      this.checkUpdates();
    }

    handleVersionAcknowledged() {
      setLSItem('CR_AppVersionUpdate', false);
      this.checkUpdates();
    }

    checkUpdates() {
      const { updateState, versionUpdateState } = this.state;
      let newState = UpdateStates.current;
      // get update check
      const updateAvailable = getLSItem('CR_AppUpdateAvailable');
      const updateAcknowledged = getLSItem('CR_AppUpdateAcknowledged');
      // get new version 1.x to 2.x check
      const newVersionUpdate = getLSItem('CR_AppVersionUpdate');
      if (updateAvailable) {
        newState = updateAcknowledged ? UpdateStates.updateAcknowledged : UpdateStates.updateAvailable;
      }
      if (newState !== updateState || newVersionUpdate !== versionUpdateState) {
        // if there's a new version, or if this is a version update (1.x -> 2.x)
        this.setState({
          updateState: newState,
          versionUpdateState: newVersionUpdate === true
        });
      }
    }

    render() {
      const { updateState, versionUpdateState } = this.state;
      const { handleAcknowledged, handleVersionAcknowledged } = this;

      let noticeVisible = false;
      let titleText = 'Update Available';
      let onOk = handleRefresh;
      let okText = 'Reload now (lose unsaved data)';
      let onCancel = handleAcknowledged;
      let cancelText = 'I will refresh later';
      let cancelButtonProps = null;
      let noticeText = <span />;
      let modalVisible = updateState === UpdateStates.updateAvailable;
      let modalMessage = (
        <messge>
          <p>A new update to the application is available!</p>
          <p>Refresh your page (after saving any changes) for the latest features</p>
        </messge>
      );
      // if there's an update state detected
      if (updateState === UpdateStates.updateAcknowledged) {
        noticeVisible = true;
        // eslint-disable-next-line
        noticeText = <span><b>&#9888;&nbsp;Update available. </b>Refresh your page (after saving any changes) to apply the update.</span>;
      }
      else if (versionUpdateState) {
        // if there's a v1.x -> 2.x user detected
        noticeVisible = true;
        titleText = 'New Release';
        onOk = handleVersionAcknowledged;
        okText = 'Ok';
        onCancel = handleVersionAcknowledged;
        cancelButtonProps = { style: { display: 'none' } };
        cancelText = 'Cancel';
        // eslint-disable-next-line
        noticeText = <span><b>&#9888;&nbsp;New Version Active. </b>You are using a new version of crew rostering.</span>;
        modalVisible = true;
        modalMessage = (
          <messge>
            <p>Welcome to the new version of Crew Rostering!</p>
            <p>Please update your bookmarks.</p>
          </messge>
        );
      }
      return (
        <div className="bhp-site-layout">
          <Modal
            className="upgrade-modal"
            title={titleText}
            visible={modalVisible}
            okText={okText}
            onOk={onOk}
            cancelText={cancelText}
            onCancel={onCancel}
            cancelButtonProps={cancelButtonProps}
            keyboard={false}
            closable={false}
            maskClosable={false}
          >
            {modalMessage}

          </Modal>
          <WrappedComponent {...this.props} />
          {noticeVisible
          && (
            <div className="bhp-update-notice">
              <div className="bhp-banner">
                {noticeText}
              </div>
            </div>
          )}
        </div>
      );
    }
  };
}
